import { useCallback, useEffect, useRef, useState } from 'react'

import { useMSEPlayer } from '../hooks/useMSEPlayer'
import { useMSEStream } from '../hooks/useMSEStream'
import * as Sentry from '@sentry/nextjs'

import { Box } from '@chakra-ui/react'
import { throttle } from 'lodash'
import screenfull from 'screenfull'

import { Spinner } from '@/components/ui'

import { VideoControlsAction } from '../types/types'
import { VideoControls } from './VideoControls'
import { VideoError } from './VideoError'

export interface MSEVideoPlayerIProps {
  cameraId: string
  autoplay?: boolean
  showControls?: boolean
  placeholderWidth?: string
  placeholderHeight?: string
  onPlayerControlAction?: (event: VideoControlsAction) => void
  onStartPlaying?: () => void
  errorLabel?: string
  onToggleLive?: (isLive: boolean) => void
  liveEnabled?: boolean
  showToggleLiveButton?: boolean
  liveOnly?: boolean
  hideSkipButtons?: boolean
}

/**
 * MSE-based video player component for authenticated proxy streaming
 */
export const MSEVideoPlayer = ({
  cameraId,
  placeholderWidth,
  placeholderHeight,
  onPlayerControlAction,
  onStartPlaying,
  errorLabel,
  autoplay = true,
  showControls = true,
  onToggleLive,
  liveEnabled = true,
  showToggleLiveButton = false,
  liveOnly = true,
  hideSkipButtons = false,
}: MSEVideoPlayerIProps) => {
  const ASPECT_RATIO_PADDING = 100 / (1280 / 720)

  const videoRef = useRef<HTMLVideoElement>(null)
  const playerContainerRef = useRef<HTMLDivElement>(null)
  const controlsRef = useRef<HTMLDivElement>(null)
  const countRef = useRef<number>(0)

  const [state, setState] = useState({
    isPlaying: false,
    played: 0,
    seeking: false,
    isPip: false,
  })

  const { isPlaying, played } = state

  // Fetch stream info with auth headers
  const {
    url,
    headers,
    isLoading: isStreamLoading,
    error: streamError,
    refetch,
  } = useMSEStream(cameraId)

  // MSE playback
  const {
    isLoading: isPlayerLoading,
    error: playerError,
    abort,
    isStreaming,
  } = useMSEPlayer({
    videoRef,
    url,
    headers,
    onCanPlay: () => {
      onStartPlaying?.()
    },
    onAuthError: () => {
      const authError = new Error(
        `MSE video player authentication error for camera: ${cameraId}`
      )
      Sentry.captureException(authError)
      refetch()
    },
  })

  const isLoading = isStreamLoading || isPlayerLoading
  const error = errorLabel || streamError || playerError

  // Auto-play when stream is ready
  useEffect(() => {
    if (videoRef.current && isStreaming && autoplay) {
      videoRef.current.play().catch((error) => {
        const autoplayError = new Error(
          `MSE video player autoplay failed for camera: ${cameraId}`
        )
        Sentry.captureException(autoplayError, {
          extra: { originalError: error },
        })
      })
      setState((prev) => ({ ...prev, isPlaying: true }))
    }
  }, [isStreaming, autoplay, cameraId])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abort()
    }
  }, [])

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
        onPlayerControlAction?.(VideoControlsAction.Pause)
      } else {
        videoRef.current.play().catch((error) => {
          const playError = new Error(
            `MSE video player play failed for camera: ${cameraId}`
          )
          Sentry.captureException(playError, {
            extra: { originalError: error },
          })
        })
        onPlayerControlAction?.(VideoControlsAction.Play)
      }
      setState((prev) => ({ ...prev, isPlaying: !prev.isPlaying }))
    }
  }

  const handleRewind = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(
        0,
        videoRef.current.currentTime - 10
      )
      onPlayerControlAction?.(VideoControlsAction.Backward10)
    }
  }

  const handleFastForward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(
        videoRef.current.currentTime + 10,
        videoRef.current.duration
      )
      onPlayerControlAction?.(VideoControlsAction.Forward10)
    }
  }

  // Throttled state update to reduce re-renders (max 4 updates/second)
  const updatePlayedProgress = useCallback(
    throttle((played: number) => {
      setState((prev) => ({ ...prev, played }))
    }, 250),
    []
  )

  const handleProgress = () => {
    if (controlsRef.current) {
      if (countRef.current > 3) {
        controlsRef.current.style.visibility = 'hidden'
        countRef.current = 0
      }
      if (controlsRef.current.style.visibility === 'visible') {
        countRef.current += 1
      }
    }

    if (!state.seeking && videoRef.current) {
      const duration = videoRef.current.duration
      const currentTime = videoRef.current.currentTime
      if (duration > 0) {
        updatePlayedProgress(currentTime / duration)
      }
    }
  }

  const handleSeekChange = (newValue: number) => {
    if (videoRef.current) {
      const value = newValue / 100
      const seekTime = value * videoRef.current.duration
      videoRef.current.currentTime = seekTime
      setState((prev) => ({ ...prev, played: value }))
    }
  }

  const toggleFullScreen = () => {
    if (screenfull.isEnabled && playerContainerRef.current) {
      screenfull.toggle(playerContainerRef.current)
    }
    onPlayerControlAction?.(VideoControlsAction.Fullscreen)
  }

  const handleMouseMove = () => {
    if (controlsRef.current) {
      controlsRef.current.style.visibility = 'visible'
      controlsRef.current.style.display = 'flex'
      countRef.current = 0
    }
  }

  const handleMouseLeave = () => {
    if (controlsRef.current) {
      controlsRef.current.style.visibility = 'hidden'
      controlsRef.current.style.display = 'none'
      countRef.current = 0
    }
  }

  // Note: PiP is not yet implemented for MSE player

  // Sync video playback state
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handlePlay = () => setState((prev) => ({ ...prev, isPlaying: true }))
    const handlePause = () =>
      setState((prev) => ({ ...prev, isPlaying: false }))

    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('timeupdate', handleProgress)

    return () => {
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('timeupdate', handleProgress)
    }
  }, [])

  const shouldShowControls = showControls && !isLoading && !error

  return (
    <Box
      bg='black'
      borderRadius='0.375rem'
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      overflow='hidden'
      position='relative'
      ref={playerContainerRef}
    >
      <Box
        minH={placeholderHeight}
        minW={placeholderWidth}
        pt={`${ASPECT_RATIO_PADDING}%`}
      >
        {error && <VideoError errorLabel={error} />}

        {isLoading && (
          <Box
            alignItems='center'
            display='flex'
            height='100%'
            justifyContent='center'
            left={0}
            position='absolute'
            top={0}
            width='100%'
          >
            <Spinner size='lg' />
          </Box>
        )}

        <video
          autoPlay={autoplay}
          data-testid='mse-video-player'
          muted
          playsInline
          ref={videoRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />

        {shouldShowControls && (
          <VideoControls
            hideSkipButtons={hideSkipButtons}
            isPlaying={isPlaying}
            liveEnabled={liveEnabled}
            liveOnly={liveOnly}
            onFastForward={handleFastForward}
            onPlayPause={handlePlayPause}
            onRewind={handleRewind}
            onSeek={handleSeekChange}
            onToggleFullScreen={toggleFullScreen}
            onToggleLive={onToggleLive}
            played={played}
            ref={controlsRef}
            showToggleLiveButton={showToggleLiveButton}
          />
        )}
      </Box>
    </Box>
  )
}
